/**
 * Audio analysis facade for the submission pipeline CRA build.
 *
 * Full Essentia.js analysis remains in katie-versions/submission/7.21
 * (`src/lib/audio-analysis.ts`). It is not bundled here because essentia.js
 * pulls Node `path`, which CRA/webpack 5 does not polyfill by default.
 *
 * Stem separation and agent flows already tolerate missing client analysis.
 */

export interface AudioFeatures {
  bpm: number;
  bpm_confidence: number;
  key: string;
  scale: string;
  key_confidence: number;
  danceability: number;
  energy: number;
  spectral_centroid: number;
  spectral_complexity: number;
  dynamic_range_db: number;
  loudness_lufs: number;
  zero_crossing_rate: number;
  genre_tags: string[];
  genre_confidences: Record<string, number>;
  is_live: boolean;
  vocal_density: string;
  stereo_width: number;
  duration_seconds: number;
}

export interface EnergyPoint {
  time_s: number;
  energy: number;
}

export interface SongSection {
  type: string;
  start_s: number;
  end_s: number;
}

export interface ChordEvent {
  time_s: number;
  chord: string;
}

export interface MoodProfile {
  valence: number;
  arousal: number;
  tags: string[];
}

export interface FullMusicMetadata {
  bpm: number;
  key_signature: string;
  time_signature: string;
  energy_profile: EnergyPoint[];
  song_structure: SongSection[];
  harmonic_content: ChordEvent[];
  genre_tags: string[];
  mood_tags: string[];
  detected_instruments: string[];
  instrument_confidence: Record<string, number>;
  frequency_range_hz: { low: number; high: number };
  danceability: number;
  spectral_centroid: number;
  dynamic_range_db: number;
  stereo_width: number;
  is_live: boolean;
  vocal_density: string;
  duration_seconds: number;
}

export function isEssentiaReady(): boolean {
  return false;
}

export async function initEssentia(): Promise<void> {
  return;
}

export async function analyzeAudio(_audioUrl: string): Promise<AudioFeatures> {
  throw new Error('Client-side Essentia analysis is not enabled in this CRA build.');
}

export async function analyzeFullMetadata(_audioUrl: string): Promise<FullMusicMetadata> {
  throw new Error('Client-side Essentia analysis is not enabled in this CRA build.');
}

export async function analyzeAudioQuick(
  _audioUrl: string,
): Promise<
  Pick<
    AudioFeatures,
    'bpm' | 'bpm_confidence' | 'key' | 'scale' | 'key_confidence' | 'energy' | 'duration_seconds'
  >
> {
  throw new Error('Client-side Essentia analysis is not enabled in this CRA build.');
}
